import React, { useContext } from 'react';
import { Handle, Position } from '@xyflow/react';
import {
  AccountTreeOutlined,
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LocationOn,
  Map,
} from '@mui/icons-material';
import {
  Typography,
  IconButton,
  Box,
  Paper,
  Divider,
  Tooltip,
  Chip,
  useTheme,
  alpha,
  Stack,
} from '@mui/material';
import { OrgSettingsContext } from '../../../../../contexts/org-settings-context';
import { IDeliveryChargeRule, DeliveryChargeRuleScope } from '../interfaces';
import { highlightZoneFunc } from '../delivery-charge-overrides';
import { red, orange, green } from '@mui/material/colors';
import { IArea } from '../../../../../interfaces/common/location';
import Decimal from 'decimal.js';
import { add, CURRENCY, displayMoney } from '../../../../../utils/money';
import { MerchantType } from '../../../../../interfaces/delivery-company/merchants';
import AmountChip from './amount-chip';

interface IAreaNodeData {
  data: {
    area:IArea;
    rules: IDeliveryChargeRule[];
    highlightZone: highlightZoneFunc;
    dimmed: boolean
    editRule: (r:IDeliveryChargeRule)=>void
    merchant: MerchantType
    readonly?: boolean
  };
}

export const AreaNode = ({ data }: IAreaNodeData) => {
  const { color } = useContext(OrgSettingsContext);
  const theme = useTheme();
  const secondaryColor = color ? alpha(color, 0.7) : theme.palette.secondary.main;

  const areaRule = data.rules.find(
    (item) =>
      item.scope === DeliveryChargeRuleScope.AREA &&
      item.area!.code === data.area.code
  );

  const rule = data.rules.find(rule => rule.scope == DeliveryChargeRuleScope.AREA && rule.area!.code == data.area.code)

  return (
    <Paper
      elevation={3}
      sx={{
        opacity: data.dimmed ? 0.01 : 1,
        filter:  data.dimmed  ? 'blur(1px)' : 'none',
        width: 280,
        borderRadius: 3,
        background: theme.palette.background.paper,
        border: `2px solid ${alpha(secondaryColor, 0.3)}`,
        boxShadow: !data.dimmed ? `0 0 15px ${alpha(red[900], 1)}` : undefined,
        fontFamily: theme.typography.fontFamily,
        transition: 'all 0.2s ease',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-2px)',
          overflow: 'visible',
          borderColor: alpha(secondaryColor, 0.6),
        },
      }}
    >
      {/* Header - Consistent with governorate but smaller */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        px={2.5}
        py={1.5}
        sx={{
          background: orange[200],
          borderTopLeftRadius: 'inherit',
          borderTopRightRadius: 'inherit',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <LocationOn
            fontSize="medium"
            sx={{ 
              color: red[700],
              fontSize: '1.8rem'
            }}
          />
          <Typography
            variant="h5"
            fontWeight={700}
            noWrap
            sx={{ 
              color: theme.palette.text.primary,
              fontSize: '1.4rem',
              lineHeight: 1.2
            }}
          >
            {data.area.name_en}
          </Typography>

        </Stack>
      </Stack>

      <Divider sx={{ 
        borderColor: alpha(theme.palette.divider, 0.2),
        borderWidth: 1 
      }} />

      {/* Content - Similar structure but compact */}
      <Box px={2.5} py={2}>
        {/* District Rule */}
        <Box mb={2}>
          <AmountChip
            deltaCharge={rule?.delta_charge}
            onClick={() => {
              if (rule)
                data.editRule(rule);
            } }    
            readonly={data.readonly}      
          />
        </Box>

        Total Fee <b>{displayMoney(add(data.merchant.default_delivery_fee, [[CURRENCY.USD, rule!.delta_charge!]]))}</b>
      </Box>



      {/* React Flow Handles */}
      <Handle
        type="target"
        position={Position.Top}
        id="top-sink"
        style={{
          opacity: 0,
          width: 0,
          height: 0,
          borderRadius: '50%',
          border: '2px solid white',
          top: -5,
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      />
    </Paper>
  );
};